import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/lib/api';
import { bootstrapAuthSession, markAuthBootstrapped, resetAuthBootstrap } from '@/lib/auth-bootstrap';
import { endLearningSession } from '@/lib/learning-session-end';
import { browserAttemptStore, logoutNotice } from '@/lib/attempt-offline-store';
import { syncStoredAttempts } from '@/lib/attempt-sync-client';
import { resolveAuthenticatedHomePath } from '@/lib/student-contract-gate';
import { selectIsAuthenticated, useAuthStore } from '@/stores/authStore';

export function useAuth() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isHydrated, setUser, markAuthenticated, clearSession } = useAuthStore();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const [sessionCheckPending, setSessionCheckPending] = useState(true);

  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => authApi.me(),
    enabled: isHydrated && isAuthenticated,
    staleTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (meQuery.data?.user) {
      setUser(meQuery.data.user);
    }
  }, [meQuery.data, setUser]);

  useEffect(() => {
    if (!isHydrated) {
      setSessionCheckPending(true);
      return;
    }

    setSessionCheckPending(true);
    bootstrapAuthSession().finally(() => setSessionCheckPending(false));
  }, [isHydrated]);

  const loginMutation = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      authApi.login(email, password),
    onSuccess: async (data) => {
      markAuthBootstrapped();
      setUser(data.user);
      markAuthenticated();
      queryClient.invalidateQueries({ queryKey: ['auth'] });
      const homePath = await resolveAuthenticatedHomePath();
      navigate(homePath);
    },
  });

  const registerMutation = useMutation({
    mutationFn: (payload: { name: string; email: string; password: string; acceptedLegalPolicyIds?: string[] }) =>
      authApi.register(payload),
    onSuccess: async (data) => {
      markAuthBootstrapped();
      setUser(data.user);
      markAuthenticated();
      queryClient.invalidateQueries({ queryKey: ['auth'] });
      const homePath = await resolveAuthenticatedHomePath();
      navigate(homePath);
    },
  });

  const logout = useCallback(async () => {
    const userId = useAuthStore.getState().user?.id;
    if (userId) {
      const drafts = browserAttemptStore().listUser(userId).map((row) => row.draft);
      const notice = logoutNotice(drafts, typeof navigator === 'undefined' ? true : navigator.onLine);
      if (notice) window.alert(notice);
      else {
        try {
          await syncStoredAttempts(userId);
        } catch {
          // El borrador sigue en este dispositivo.
        }
      }
    }
    try {
      await endLearningSession('logout');
      await authApi.logout();
    } finally {
      resetAuthBootstrap();
      clearSession();
      queryClient.clear();
      navigate('/login');
    }
  }, [clearSession, navigate, queryClient]);

  return {
    user,
    isAuthenticated,
    isHydrated,
    isLoading:
      !isHydrated ||
      sessionCheckPending ||
      (isAuthenticated && meQuery.isLoading),
    login: loginMutation.mutateAsync,
    loginError: loginMutation.error,
    isLoggingIn: loginMutation.isPending,
    register: registerMutation.mutateAsync,
    registerError: registerMutation.error,
    isRegistering: registerMutation.isPending,
    logout,
  };
}
